/**
 * ProductionEditorProvider — lightweight prototyping wrapper around @xyflow/react.
 *
 * Mirrors the API spirit of the real Production Editor in size2mix-next-frontend
 * (`src/ui/views/context/ProductionEditorContext.tsx`) but with only what's
 * needed to prototype around it: nodes/edges state, drag-and-drop from a
 * sidebar, selection. No full-screen, no real modals, no domain types.
 *
 * Reference (real impl): docs/production-editor pattern in size2mix-next-frontend.
 */
import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type MutableRefObject,
    type ReactNode,
    type SetStateAction,
} from 'react';
import {
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
    type Edge,
    type Node,
    type OnEdgesChange,
    type OnNodesChange,
} from '@xyflow/react';

export interface DraggedItem {
    id: string;
    label: string;
    type: string;
    data?: Record<string, unknown>;
}

export interface ProductionEditorContextValue {
    nodes: Node[];
    setNodes: Dispatch<SetStateAction<Node[]>>;
    edges: Edge[];
    setEdges: Dispatch<SetStateAction<Edge[]>>;
    onNodesChange: OnNodesChange<Node>;
    onEdgesChange: OnEdgesChange<Edge>;
    draggedItem: DraggedItem | undefined;
    draggedItemRef: MutableRefObject<DraggedItem | undefined>;
    setDraggedItem: (item: DraggedItem | undefined) => void;
    selectedNodes: Node[];
    setSelectedNodes: (nodes: Node[]) => void;
    filter: Record<string, boolean>;
    setFilter: (filter: Record<string, boolean>) => void;
}

const ProductionEditorContext = createContext<
    ProductionEditorContextValue | undefined
>(undefined);

interface ProductionEditorProviderProps {
    initialNodes?: Node[];
    initialEdges?: Edge[];
    initialFilter?: Record<string, boolean>;
    children: ReactNode;
}

export function ProductionEditorProvider({
    initialNodes = [],
    initialEdges = [],
    initialFilter = { asset: true, contract: true, group: true },
    children,
}: ProductionEditorProviderProps) {
    const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);
    const draggedItemRef = useRef<DraggedItem | undefined>(undefined);
    const [draggedItem, setDraggedItemState] = useState<DraggedItem | undefined>(
        undefined,
    );
    const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);
    const [filter, setFilter] = useState<Record<string, boolean>>(initialFilter);

    const setDraggedItem = useCallback((item: DraggedItem | undefined) => {
        draggedItemRef.current = item;
        setDraggedItemState(item);
    }, []);

    const value = useMemo<ProductionEditorContextValue>(
        () => ({
            nodes,
            setNodes,
            edges,
            setEdges,
            onNodesChange,
            onEdgesChange,
            draggedItem,
            draggedItemRef,
            setDraggedItem,
            selectedNodes,
            setSelectedNodes,
            filter,
            setFilter,
        }),
        [
            nodes,
            setNodes,
            edges,
            setEdges,
            onNodesChange,
            onEdgesChange,
            draggedItem,
            setDraggedItem,
            selectedNodes,
            filter,
        ],
    );

    return (
        <ReactFlowProvider>
            <ProductionEditorContext.Provider value={value}>
                {children}
            </ProductionEditorContext.Provider>
        </ReactFlowProvider>
    );
}

export function useProductionEditor(): ProductionEditorContextValue {
    const ctx = useContext(ProductionEditorContext);
    if (!ctx) {
        throw new Error(
            'useProductionEditor must be used inside <ProductionEditorProvider>',
        );
    }
    return ctx;
}
